import { cn } from '@/lib/utils';
import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  FormFieldGroup,
  ImageUploadDropzone,
  Input,
  Label,
  Switch,
  toast,
  useApiClient,
} from '@startupquickstart/frontend';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { QRCode } from '@/components/qr/QRCode';
import {
  Anchor,
  Bird,
  Compass,
  Flag,
  Flame,
  Heart,
  type LucideIcon,
  Map,
  Mountain,
  Rocket,
  Shield,
  Ship,
  Star,
  Sun,
  Target,
  Trophy,
  Users,
  Zap,
} from 'lucide-react';
import { useState } from 'react';

// Available icons for teams
const TEAM_ICONS: { name: string; icon: LucideIcon }[] = [
  { name: 'users', icon: Users },
  { name: 'trophy', icon: Trophy },
  { name: 'star', icon: Star },
  { name: 'flag', icon: Flag },
  { name: 'rocket', icon: Rocket },
  { name: 'flame', icon: Flame },
  { name: 'zap', icon: Zap },
  { name: 'shield', icon: Shield },
  { name: 'target', icon: Target },
  { name: 'compass', icon: Compass },
  { name: 'map', icon: Map },
  { name: 'mountain', icon: Mountain },
  { name: 'sun', icon: Sun },
  { name: 'anchor', icon: Anchor },
  { name: 'ship', icon: Ship },
  { name: 'bird', icon: Bird },
  { name: 'heart', icon: Heart },
];

// Available colors for teams
const TEAM_COLORS = [
  { name: 'Teal', value: '#14B8A6' },
  { name: 'Blue', value: '#3B82F6' },
  { name: 'Indigo', value: '#6366F1' },
  { name: 'Purple', value: '#8B5CF6' },
  { name: 'Pink', value: '#EC4899' },
  { name: 'Red', value: '#EF4444' },
  { name: 'Orange', value: '#F97316' },
  { name: 'Amber', value: '#F59E0B' },
  { name: 'Green', value: '#22C55E' },
  { name: 'Emerald', value: '#10B981' },
];

interface AllianceOption {
  id: string;
  name: string;
  iconName: string;
  iconColor: string;
}

interface Team {
  id: string;
  name: string;
  code?: string;
  iconName: string;
  iconColor: string;
  photoUrl?: string | null;
  allianceId?: string | null;
  isStaff?: boolean;
  /** null = full facilitator access, [] = read-only, subset = scoped. */
  staffPermissions?: string[] | null;
}

type StaffAccessChoice = 'full' | 'readonly' | 'custom';

const STAFF_PERMISSION_OPTIONS: { key: string; label: string; description: string }[] = [
  { key: 'points', label: 'Points', description: 'Award or deduct team points' },
  { key: 'chat', label: 'Chat', description: 'Reply to teams as facilitator' },
  { key: 'review', label: 'Review', description: 'Approve or reject submissions' },
  { key: 'controls', label: 'Controls', description: 'Start, pause, end; live toggles' },
  { key: 'effects', label: 'Effects', description: 'Apply power-up effects' },
];

function accessChoiceFor(permissions: string[] | null | undefined): StaffAccessChoice {
  if (permissions === null || permissions === undefined) return 'full';
  if (permissions.length === 0) return 'readonly';
  return 'custom';
}

interface TeamEditDialogProps {
  eventId: string;
  team: Team;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function TeamEditDialog({ eventId, team, open, onOpenChange }: TeamEditDialogProps) {
  const apiClient = useApiClient();
  const queryClient = useQueryClient();
  const [name, setName] = useState(team.name);
  const [iconName, setIconName] = useState(team.iconName || 'users');
  const [iconColor, setIconColor] = useState(team.iconColor || '#14B8A6');
  const [photoUrl, setPhotoUrl] = useState<string | undefined>(team.photoUrl ?? undefined);
  const [allianceId, setAllianceId] = useState<string | null>(team.allianceId || null);
  const [isStaff, setIsStaff] = useState<boolean>(team.isStaff ?? false);
  const [staffAccess, setStaffAccess] = useState<StaffAccessChoice>(
    accessChoiceFor(team.staffPermissions),
  );
  const [customPermissions, setCustomPermissions] = useState<string[]>(
    Array.isArray(team.staffPermissions) ? team.staffPermissions : [],
  );
  const [errors, setErrors] = useState<Record<string, string>>({});

  // Fetch alliances for this event
  const { data: alliancesData } = useQuery<{ success: boolean; data: AllianceOption[] }>({
    queryKey: ['EventAlliances', eventId],
    queryFn: () => apiClient.get(`/events/${eventId}/alliances`),
    enabled: !!eventId,
  });
  const alliances = alliancesData?.data || [];

  const updateMutation = useMutation({
    mutationFn: async () => {
      return apiClient.patch(`/events/${eventId}/teams/${team.id}`, {
        name: name.trim(),
        iconName,
        iconColor,
        // `null` clears the photo back to the icon fallback; the API
        // schema accepts both `null` and a string URL.
        photoUrl: photoUrl ?? null,
        allianceId: allianceId || null,
        isStaff,
        // Only meaningful for staff teams: null = full access,
        // [] = read-only observer, subset = scoped.
        staffPermissions: !isStaff
          ? null
          : staffAccess === 'full'
            ? null
            : staffAccess === 'readonly'
              ? []
              : customPermissions,
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['EventTeams', eventId] });
      queryClient.invalidateQueries({ queryKey: ['EventAlliances', eventId] });
      toast.success('Team updated successfully');
      onOpenChange(false);
    },
    onError: (err: Error) => {
      toast.error(err?.message || 'Failed to update team');
    },
  });

  const validate = (): boolean => {
    const errs: Record<string, string> = {};

    if (!name.trim()) {
      errs.name = 'Team name is required';
    } else if (name.trim().length > 100) {
      errs.name = 'Team name must be 100 characters or less';
    }

    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const handleSubmit = () => {
    if (!validate()) return;
    updateMutation.mutate();
  };

  // Get the current icon component
  const CurrentIcon = TEAM_ICONS.find((i) => i.name === iconName)?.icon || Users;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Edit Team</DialogTitle>
          <DialogDescription>Update the team's name, icon, and color.</DialogDescription>
        </DialogHeader>
        <div className="space-y-5 py-4">
          {/* Preview — photo wins when set */}
          <div className="flex items-center justify-center">
            {photoUrl ? (
              <img
                src={photoUrl}
                alt={`${name} team photo`}
                className="h-16 w-16 rounded-full object-cover"
              />
            ) : (
              <div
                className="flex items-center justify-center h-16 w-16 rounded-full"
                style={{ backgroundColor: `${iconColor}20` }}
              >
                <CurrentIcon className="h-8 w-8" style={{ color: iconColor }} />
              </div>
            )}
          </div>

          {/* Team photo upload — overrides the icon when set */}
          <ImageUploadDropzone
            label="Team photo (optional)"
            value={photoUrl}
            onChange={(url) => setPhotoUrl(url)}
            targetAspectRatio={1}
            maxSizeMB={5}
          />

          {/* Team QR — encodes the join code so members can scan with
              the mobile app instead of typing. Print this for the
              physical packet at the event. */}
          {team.code && (
            <div className="flex flex-col items-center gap-2 rounded-md border border-border bg-muted/20 p-4">
              <Label className="text-xs uppercase text-muted-foreground">Team QR Code</Label>
              <QRCode value={team.code} size={160} alt={`QR code for team code ${team.code}`} />
              <p className="text-center text-xs text-muted-foreground">
                Scan in the mobile app to auto-fill the team code.
              </p>
              <code className="rounded bg-muted px-2 py-1 font-mono text-xs">{team.code}</code>
            </div>
          )}

          <FormFieldGroup name="name" label="Team Name" required error={errors.name}>
            <Input
              id="name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Enter team name"
              className={cn(errors.name && 'border-destructive')}
            />
          </FormFieldGroup>

          {/* Icon Picker */}
          <div className="space-y-2">
            <Label>Icon</Label>
            <div className="grid grid-cols-6 gap-2">
              {TEAM_ICONS.map((item) => {
                const Icon = item.icon;
                const isSelected = item.name === iconName;
                return (
                  <button
                    key={item.name}
                    type="button"
                    onClick={() => setIconName(item.name)}
                    className={cn(
                      'flex items-center justify-center h-10 w-10 rounded-lg border transition-all',
                      isSelected
                        ? 'border-primary bg-primary/10 ring-2 ring-primary ring-offset-1'
                        : 'border-muted hover:border-primary/50 hover:bg-muted/50',
                    )}
                  >
                    <Icon
                      className="h-5 w-5"
                      style={{ color: isSelected ? iconColor : undefined }}
                    />
                  </button>
                );
              })}
            </div>
          </div>

          {/* Color Picker */}
          <div className="space-y-2">
            <Label>Color</Label>
            <div className="flex flex-wrap gap-2">
              {TEAM_COLORS.map((color) => {
                const isSelected = color.value === iconColor;
                return (
                  <button
                    key={color.value}
                    type="button"
                    onClick={() => setIconColor(color.value)}
                    className={cn(
                      'h-8 w-8 rounded-full transition-all',
                      isSelected && 'ring-2 ring-offset-2 ring-primary',
                    )}
                    style={{ backgroundColor: color.value }}
                    title={color.name}
                  />
                );
              })}
            </div>
          </div>

          {/* Staff team toggle */}
          <FormFieldGroup
            name="isStaff"
            label="Staff team"
            description="Excluded from leaderboard and from speed-bonus / max-completion counts. Use for facilitators monitoring or testing the event."
          >
            <div
              role="button"
              className="flex items-center gap-2 cursor-pointer"
              onClick={() => setIsStaff((v) => !v)}
            >
              <Switch checked={isStaff} tabIndex={-1} />
              <span className="text-sm font-medium">
                {isStaff ? 'Staff — non-competing' : 'Regular team'}
              </span>
            </div>
          </FormFieldGroup>

          {/* Staff access tier. Members joining with this team's code get
              exactly these powers in the mobile facilitator hub; the API
              enforces the same rules server-side. */}
          {isStaff && (
            <FormFieldGroup
              name="staffAccess"
              label="Staff access"
              description="What members joining with this code can do in the facilitator hub."
            >
              <div className="space-y-2">
                <div className="flex gap-2">
                  {(
                    [
                      { value: 'full', label: 'Full access' },
                      { value: 'readonly', label: 'Read-only' },
                      { value: 'custom', label: 'Custom' },
                    ] as const
                  ).map((option) => (
                    <button
                      key={option.value}
                      type="button"
                      onClick={() => setStaffAccess(option.value)}
                      className={cn(
                        'flex-1 rounded-md border px-3 py-2 text-sm font-medium transition-all',
                        staffAccess === option.value
                          ? 'border-primary bg-primary/10 text-primary'
                          : 'border-muted hover:border-primary/50',
                      )}
                    >
                      {option.label}
                    </button>
                  ))}
                </div>
                {staffAccess === 'readonly' && (
                  <p className="text-xs text-muted-foreground">
                    Can watch standings, activity, the live map, the help inbox, and the review
                    queue — but can't change anything.
                  </p>
                )}
                {staffAccess === 'custom' && (
                  <div className="space-y-1.5 rounded-md border border-border p-3">
                    {STAFF_PERMISSION_OPTIONS.map((permission) => {
                      const checked = customPermissions.includes(permission.key);
                      return (
                        <label
                          key={permission.key}
                          className="flex cursor-pointer items-center gap-2 text-sm"
                        >
                          <input
                            type="checkbox"
                            checked={checked}
                            onChange={() =>
                              setCustomPermissions((prev) =>
                                checked
                                  ? prev.filter((k) => k !== permission.key)
                                  : [...prev, permission.key],
                              )
                            }
                          />
                          <span className="font-medium">{permission.label}</span>
                          <span className="text-xs text-muted-foreground">
                            {permission.description}
                          </span>
                        </label>
                      );
                    })}
                  </div>
                )}
              </div>
            </FormFieldGroup>
          )}

          {/* Alliance Dropdown */}
          {alliances.length > 0 && (
            <FormFieldGroup name="alliance" label="Alliance" description="Assign this team to an alliance">
              <select
                id="alliance"
                value={allianceId || ''}
                onChange={(e) => setAllianceId(e.target.value || null)}
                className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
              >
                <option value="">No alliance</option>
                {alliances.map((a) => (
                  <option key={a.id} value={a.id}>
                    {a.name}
                  </option>
                ))}
              </select>
            </FormFieldGroup>
          )}
        </div>
        <div className="flex justify-end gap-3">
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button onClick={handleSubmit} loading={updateMutation.isPending}>
            Save Changes
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

// Export the icons list for use elsewhere if needed
export { TEAM_COLORS, TEAM_ICONS };
